import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useChartTheme } from '../../utils/chartTheme';
import { ResponseSource, SeriesPoint, TimeWindow, timeTickFormatter } from '../../utils/series';

interface Props {
  /** Built by selectResponseSeries, so the caller can also label the source. */
  data: SeriesPoint[];
  source: ResponseSource;
  /** The selected range; the x-axis spans exactly this (see windowBounds). */
  timeWindow: TimeWindow;
}

export function ResponseTimeChart({ data, source, timeWindow }: Props) {
  const t = useChartTheme();
  const seriesLabel = source === 'hourly' ? 'Hourly avg' : 'Response time';
  // Tick format follows the window, not the data: a day-old service on the 7d
  // view still gets date labels across the whole axis.
  const format = timeTickFormatter(timeWindow[1] - timeWindow[0]);

  if (data.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400 text-sm">
        No response times recorded in this window.
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={250}>
      <LineChart data={data} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={t.grid} vertical={false} />
        <XAxis
          dataKey="t"
          type="number"
          scale="time"
          domain={timeWindow}
          allowDataOverflow
          tickFormatter={format}
          tick={{ fontSize: 11, fill: t.axis }}
          stroke={t.grid}
          minTickGap={40}
        />
        <YAxis tick={{ fontSize: 11, fill: t.axis }} stroke={t.grid} unit="ms" width={56} />
        <Tooltip
          contentStyle={t.tooltip}
          labelStyle={{ color: t.axis }}
          cursor={{ stroke: t.grid }}
          labelFormatter={(v: number) => new Date(v).toLocaleString()}
          formatter={(value: number) => [`${Math.round(value)} ms`, seriesLabel]}
        />
        {/* connectNulls={false} is the point: a gap in checks draws as a gap. */}
        <Line
          type="monotone"
          dataKey="responseTime"
          stroke={t.line}
          strokeWidth={2}
          dot={false}
          connectNulls={false}
          isAnimationActive={false}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}
